import { useMemo, useState, Suspense } from "react";
import * as THREE from "three";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Edges, Html, ContactShadows } from "@react-three/drei";

// Stylized-realistic 3D massing of Villa Tutto Passa (wood cladding, glass bays, flat dark roofs)
const GROUND_H = 3;
const UP_H = 2.6;
const UP_Y = GROUND_H + 0.3;

const BLOCKS = [
  { id: "chambre5", args: [3.4, GROUND_H, 4.2], pos: [-7.2, GROUND_H / 2, 0.6], rot: 0.12, win: 0.5 },
  { id: "chambre4", args: [3.0, GROUND_H, 3.4], pos: [-4.2, GROUND_H / 2, 1.0], rot: 0.12, win: 0.5 },
  { id: "cointv", args: [2.0, GROUND_H, 2.6], pos: [-3.1, GROUND_H / 2, -2.2], rot: 0.12, win: 0 },
  { id: "salon", args: [7, GROUND_H, 6.5], pos: [0.8, GROUND_H / 2, -0.55], rot: 0.12, win: 0.92, winRight: 0.7, chimney: true },
  { id: "cuisine", args: [3.6, GROUND_H, 4.2], pos: [5.6, GROUND_H / 2, -2.4], rot: 0.12, win: 0.85 },
  { id: "sdb", args: [2.2, GROUND_H, 2.4], pos: [8.6, GROUND_H / 2, -2.4], rot: 0.12, win: 0.3 },
  { id: "chambre3", args: [3.6, GROUND_H, 4.4], pos: [9.4, GROUND_H / 2, 1.4], rot: 0.5, win: 0.7 },
  { id: "chambre1", args: [4.6, UP_H, 4.2], pos: [-5.6, UP_Y + UP_H / 2, 0.2], rot: 0.12, win: 0.85 },
  { id: "chambre2", args: [4.2, UP_H, 3.8], pos: [8.6, UP_Y + UP_H / 2, 0.6], rot: 0.5, win: 0.85 },
];

const ROOFS = [
  { args: [4, 4.8], pos: [-7.2, GROUND_H, 0.6], rot: 0.12 },
  { args: [3.6, 4.0], pos: [-4.2, GROUND_H, 1.0], rot: 0.12 },
  { args: [8, 7.5], pos: [0.8, GROUND_H, -0.55], rot: 0.12 },
  { args: [4.4, 5.0], pos: [5.6, GROUND_H, -2.4], rot: 0.12 },
  { args: [2.8, 3.0], pos: [8.6, GROUND_H, -2.4], rot: 0.12 },
  { args: [2.6, 3.2], pos: [-3.1, GROUND_H, -2.2], rot: 0.12 },
  { args: [4.2, 5.0], pos: [9.4, GROUND_H, 1.4], rot: 0.5 },
  { args: [5.2, 4.8], pos: [-5.6, UP_Y + UP_H, 0.2], rot: 0.12 },
  { args: [4.8, 4.4], pos: [8.6, UP_Y + UP_H, 0.6], rot: 0.5 },
];

const TERRACES = [
  { pos: [-5.6, UP_Y - 0.09, 0.2], rot: 0.12, w: 4.6, d: 4.2 },
  { pos: [8.6, UP_Y - 0.09, 0.6], rot: 0.5, w: 4.2, d: 3.8 },
];

const BUSHES = [
  [-11, 0.7, 3, 1.1], [-9.5, 0.5, 6.5, 0.8], [5.5, 0.6, -6.5, 1.0], [8.5, 0.5, -6, 0.8],
  [12.5, 0.6, 5, 1.0], [13, 0.5, -1, 0.9], [-11.5, 0.4, -2, 0.7], [7, 0.5, 11.5, 0.9],
  [-6, 0.5, 11, 0.8], [11.5, 0.5, 9.5, 0.8],
];
const ROCKS = [
  [-9.8, 4.8, 0.55], [-4.5, 10.8, 0.45], [11.8, 7.6, 0.6], [-11, 8.5, 0.5], [13.2, 2.8, 0.42],
];

function usePlanks(base, dark, horizontal = true) {
  return useMemo(() => {
    const c = document.createElement("canvas");
    c.width = 256; c.height = 256;
    const g = c.getContext("2d");
    g.fillStyle = base; g.fillRect(0, 0, 256, 256);
    g.strokeStyle = dark; g.lineWidth = 1.4; g.globalAlpha = 0.45;
    for (let i = 0; i < 256; i += 13) {
      g.beginPath();
      if (horizontal) { g.moveTo(0, i); g.lineTo(256, i); } else { g.moveTo(i, 0); g.lineTo(i, 256); }
      g.stroke();
      g.globalAlpha = 0.18 + 0.3 * Math.random();
    }
    const t = new THREE.CanvasTexture(c);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(2, 2);
    return t;
  }, [base, dark, horizontal]);
}

const label3d = {
  background: "#2D302A", color: "#F5F1E8", padding: "4px 12px",
  fontSize: "11px", letterSpacing: "0.08em", whiteSpace: "nowrap",
  fontFamily: "Manrope, sans-serif", pointerEvents: "none",
};

const Bay = ({ width, height, night }) => (
  <>
    <mesh>
      <boxGeometry args={[width, height * 0.78, 0.06]} />
      <meshStandardMaterial color="#20211F" roughness={0.5} metalness={0.3} />
    </mesh>
    <mesh position={[0, 0, 0.035]}>
      <meshPhysicalMaterial
        color={night ? "#FFCF9A" : "#9FBFC2"}
        emissive={night ? "#FFA84E" : "#000000"}
        emissiveIntensity={night ? 1.2 : 0}
        roughness={0.08}
        metalness={night ? 0 : 0.35}
        transparent
        opacity={night ? 0.96 : 0.55}
      />
      <boxGeometry args={[width * 0.94, height * 0.7, 0.02]} />
    </mesh>
    {[...Array(3)].map((_, i) => (
      <mesh key={i} position={[(i - 1) * width * 0.3, 0, 0.05]}>
        <boxGeometry args={[0.04, height * 0.7, 0.03]} />
        <meshStandardMaterial color="#1B1C1A" />
      </mesh>
    ))}
  </>
);

const Block = ({ id, args, pos, rot, win, winRight, chimney, active, onSelect, label, woodMap, night }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <group position={pos} rotation={[0, rot, 0]}>
      <mesh
        onClick={(e) => { e.stopPropagation(); onSelect(id); }}
        onPointerOver={(e) => { e.stopPropagation(); setHovered(true); document.body.style.cursor = "pointer"; }}
        onPointerOut={() => { setHovered(false); document.body.style.cursor = "auto"; }}
      >
        <boxGeometry args={args} />
        <meshStandardMaterial
          map={woodMap}
          color={active ? "#C1714B" : hovered ? "#E8CCA2" : night ? "#8F7B5C" : "#DBBE93"}
          roughness={0.8}
        />
        <Edges threshold={20} color="#5A4A38" />
      </mesh>

      {/* glass bay facing the pool (dark frame + glazing) */}
      {win > 0 && (
        <group position={[0, -args[1] * 0.06, args[2] / 2 + 0.05]}>
          <Bay width={args[0] * win} height={args[1]} night={night} />
        </group>
      )}

      {/* glass bay on the right side (facing the kitchen) */}
      {winRight > 0 && (
        <group position={[args[0] / 2 + 0.05, -args[1] * 0.06, 0]} rotation={[0, Math.PI / 2, 0]}>
          <Bay width={args[2] * winRight} height={args[1]} night={night} />
        </group>
      )}

      {chimney && (
        <mesh position={[args[0] * 0.28, args[1] / 2 + 1.3, -args[2] * 0.2]}>
          <cylinderGeometry args={[0.09, 0.09, 2.6, 16]} />
          <meshStandardMaterial color="#1A1A18" roughness={0.5} metalness={0.4} />
        </mesh>
      )}

      {active && (
        <Html center position={[0, args[1] / 2 + 1.05, 0]} zIndexRange={[10, 0]}>
          <div style={label3d}>{label}</div>
        </Html>
      )}
    </group>
  );
};

const Scene = ({ active, onSelect, labels, night, paused }) => {
  const cladding = usePlanks("#D8BE93", "#9A7E58", true);
  const decking = usePlanks("#B98F63", "#7E6141", false);

  return (
    <>
      <ambientLight intensity={night ? 0.3 : 0.75} color={night ? "#8FA3C8" : "#ffffff"} />
      <directionalLight position={[12, 18, 10]} intensity={night ? 0.3 : 1.5} color={night ? "#AFC4E8" : "#FFF3E0"} />
      <directionalLight position={[-10, 8, -6]} intensity={night ? 0.12 : 0.35} color="#DCE8F0" />
      {night && (
        <>
          <pointLight position={[1.2, 2.4, 1.8]} intensity={9} color="#FFB874" distance={14} decay={1.4} />
          <pointLight position={[1.2, 1.2, 8.4]} intensity={6} color="#5FD3E8" distance={9} decay={1.4} />
          <pointLight position={[-5.2, 4.6, 3.4]} intensity={5} color="#FFB874" distance={8} decay={1.4} />
          <pointLight position={[7.4, 4.6, 3.2]} intensity={5} color="#FFB874" distance={8} decay={1.4} />
        </>
      )}

      {/* ground */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.05, 2]}>
        <circleGeometry args={[24, 64]} />
        <meshStandardMaterial color={night ? "#4E4A3E" : "#DED4BC"} roughness={1} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.04, 2]}>
        <ringGeometry args={[16, 24, 64]} />
        <meshStandardMaterial color={night ? "#39412F" : "#A8AF8A"} roughness={1} />
      </mesh>

      {/* wooden deck */}
      <mesh
        position={[0.8, 0.1, 7.4]}
        onClick={(e) => { e.stopPropagation(); onSelect("terrasse"); }}
        onPointerOver={() => (document.body.style.cursor = "pointer")}
        onPointerOut={() => (document.body.style.cursor = "auto")}
      >
        <boxGeometry args={[17, 0.2, 7.2]} />
        <meshStandardMaterial map={decking} color={active === "terrasse" ? "#C98D6B" : night ? "#7A6248" : "#C29A6B"} roughness={0.85} />
        <Edges threshold={20} color="#6B5236" />
      </mesh>

      {/* pool: dark rim + turquoise water */}
      <group
        onClick={(e) => { e.stopPropagation(); onSelect("piscine"); }}
        onPointerOver={() => (document.body.style.cursor = "pointer")}
        onPointerOut={() => (document.body.style.cursor = "auto")}
      >
        <mesh position={[1.2, 0.22, 8.4]}>
          <boxGeometry args={[7.4, 0.16, 3.3]} />
          <meshStandardMaterial color="#33383A" roughness={0.6} />
        </mesh>
        <mesh position={[1.2, 0.31, 8.4]}>
          <boxGeometry args={[7.0, 0.06, 2.95]} />
          <meshPhysicalMaterial
            color={active === "piscine" ? "#C98D6B" : night ? "#2FC4DE" : "#57B4C4"}
            emissive={night ? "#1E96AE" : "#000000"}
            emissiveIntensity={night ? 0.9 : 0}
            roughness={0.05}
            metalness={0.1}
            transparent
            opacity={0.92}
          />
        </mesh>
        <mesh position={[4.05, 0.345, 8.4]}>
          <boxGeometry args={[1.0, 0.05, 2.9]} />
          <meshStandardMaterial color={night ? "#7B8285" : "#AEB4B2"} roughness={0.85} />
        </mesh>
      </group>
      {(active === "terrasse" || active === "piscine") && (
        <Html center position={active === "piscine" ? [1.2, 1.5, 8.4] : [-4, 1.3, 7.6]} zIndexRange={[10, 0]}>
          <div style={label3d}>{labels[active]}</div>
        </Html>
      )}

      {/* daybeds à gauche de la piscine + parasol */}
      {[[-5.6, 7.2], [-4.2, 8.2], [-5.4, 9.3]].map(([x, z], i) => (
        <group key={i} position={[x, 0.32, z]} rotation={[0, i * 0.25 - 0.25, 0]}>
          <mesh><boxGeometry args={[1.5, 0.26, 0.95]} /><meshStandardMaterial color="#E9E4D6" roughness={0.9} /></mesh>
        </group>
      ))}
      <group position={[-3.6, 0, 6.6]}>
        <mesh position={[0, 1.25, 0]}><cylinderGeometry args={[0.03, 0.03, 2.5, 8]} /><meshStandardMaterial color="#4A473E" /></mesh>
        <mesh position={[0, 2.45, 0]}><coneGeometry args={[1.15, 0.55, 8]} /><meshStandardMaterial color="#CFC8B6" roughness={0.9} /></mesh>
      </group>

      {/* cuisine d'été couverte, à droite de la piscine */}
      <group position={[7.2, 0, 8.6]}>
        {[[-1.4, -1.0], [1.4, -1.0], [-1.4, 1.0], [1.4, 1.0]].map(([x, z], i) => (
          <mesh key={i} position={[x, 1.15, z]}>
            <cylinderGeometry args={[0.07, 0.07, 2.3, 10]} />
            <meshStandardMaterial color={night ? "#7E6238" : "#B98F5B"} roughness={0.9} />
          </mesh>
        ))}
        <mesh position={[0, 2.38, 0]}>
          <boxGeometry args={[3.4, 0.14, 2.6]} />
          <meshStandardMaterial map={decking} color={night ? "#7C6748" : "#D8B87E"} roughness={0.95} />
          <Edges threshold={20} color="#8A6F42" />
        </mesh>
        <mesh position={[0, 0.55, -0.6]}>
          <boxGeometry args={[2.8, 0.9, 0.7]} />
          <meshStandardMaterial color="#33383A" roughness={0.8} />
          <Edges threshold={20} color="#1F2224" />
        </mesh>
      </group>

      {/* room volumes */}
      {BLOCKS.map((b) => (
        <Block key={b.id} {...b} night={night} active={active === b.id} onSelect={onSelect} label={labels[b.id]} woodMap={cladding} />
      ))}

      {/* auvent bois continu au-dessus des baies (face piscine) */}
      <mesh position={[1.3, GROUND_H - 0.22, 3.5]} rotation={[0, 0.12, 0]}>
        <boxGeometry args={[6.8, 0.14, 1.7]} />
        <meshStandardMaterial map={cladding} color={night ? "#8A7148" : "#DCBB84"} roughness={0.9} />
        <Edges threshold={20} color="#8A6F42" />
      </mesh>
      <mesh position={[5.95, GROUND_H - 0.22, 0.45]} rotation={[0, 0.12, 0]}>
        <boxGeometry args={[3.8, 0.14, 1.6]} />
        <meshStandardMaterial map={cladding} color={night ? "#8A7148" : "#DCBB84"} roughness={0.9} />
      </mesh>

      {/* entrée vitrée côté parking (dos du salon), sous auvent bois */}
      <group position={[0.36, 0, -3.85]} rotation={[0, 0.12, 0]}>
        <group
          position={[0, GROUND_H / 2 - GROUND_H * 0.06, -0.08]}
          rotation={[0, Math.PI, 0]}
          onClick={(e) => { e.stopPropagation(); onSelect("sdb"); }}
          onPointerOver={() => (document.body.style.cursor = "pointer")}
          onPointerOut={() => (document.body.style.cursor = "auto")}
        >
          <Bay width={4.6} height={GROUND_H} night={night} />
        </group>
        <mesh position={[0, GROUND_H - 0.22, -0.85]}>
          <boxGeometry args={[5.4, 0.14, 1.7]} />
          <meshStandardMaterial map={cladding} color={night ? "#8A7148" : "#DCBB84"} roughness={0.9} />
          <Edges threshold={20} color="#8A6F42" />
        </mesh>
        {night && <pointLight position={[0, 2.2, -1.2]} intensity={4} color="#FFB874" distance={6} decay={1.4} />}
        {active === "sdb" && (
          <Html center position={[0, GROUND_H + 0.8, 0]} zIndexRange={[10, 0]}>
            <div style={label3d}>{labels["sdb"]}</div>
          </Html>
        )}
      </group>

      {/* couronnes pergola canisse au-dessus des suites R+1 */}
      {[
        { pos: [-5.6, UP_Y + UP_H + 0.5, 0.2], rot: 0.12, args: [5.4, 5.0] },
        { pos: [8.6, UP_Y + UP_H + 0.5, 0.6], rot: 0.5, args: [5.0, 4.6] },
      ].map((p, i) => (
        <group key={`crown${i}`} position={p.pos} rotation={[0, p.rot, 0]}>
          <mesh>
            <boxGeometry args={[p.args[0], 0.1, p.args[1]]} />
            <meshStandardMaterial map={decking} color={night ? "#7C6748" : "#DCBE85"} roughness={0.95} />
          </mesh>
          {[...Array(4)].map((_, j) => (
            <mesh key={j} position={[0, -0.12, -p.args[1] / 2 + 0.4 + (j * (p.args[1] - 0.8)) / 3]}>
              <boxGeometry args={[p.args[0], 0.14, 0.12]} />
              <meshStandardMaterial color={night ? "#6E5330" : "#B08650"} />
            </mesh>
          ))}
        </group>
      ))}

      {/* flat dark roofs with wood fascia */}
      {ROOFS.map((r, i) => (
        <group key={i} position={r.pos} rotation={[0, r.rot, 0]}>
          <mesh position={[0, 0.24, 0]}>
            <boxGeometry args={[r.args[0], 0.12, r.args[1]]} />
            <meshStandardMaterial color="#2E2C26" roughness={0.7} />
            <Edges threshold={20} color="#191813" />
          </mesh>
          <mesh position={[0, 0.09, 0]}>
            <boxGeometry args={[r.args[0] - 0.12, 0.18, r.args[1] - 0.12]} />
            <meshStandardMaterial map={cladding} color="#D3B287" roughness={0.85} />
          </mesh>
        </group>
      ))}

      {/* R+1 L-shaped wooden terraces: front full width + left side return */}
      {TERRACES.map((t2, i) => (
        <group key={i} position={t2.pos} rotation={[0, t2.rot, 0]}>
          <mesh position={[0, 0, t2.d / 2 + 0.9]}>
            <boxGeometry args={[t2.w, 0.18, 1.8]} />
            <meshStandardMaterial map={decking} color="#C29A6B" roughness={0.85} />
          </mesh>
          <mesh position={[-(t2.w / 2 + 0.8), 0, 0.9]}>
            <boxGeometry args={[1.6, 0.18, t2.d + 1.8]} />
            <meshStandardMaterial map={decking} color="#C29A6B" roughness={0.85} />
          </mesh>
          <mesh position={[-0.8, 0.35, t2.d / 2 + 1.77]}>
            <boxGeometry args={[t2.w + 1.6, 0.5, 0.04]} />
            <meshPhysicalMaterial color="#B9CDD0" roughness={0.1} transparent opacity={0.4} />
          </mesh>
          <mesh position={[-(t2.w / 2 + 1.57), 0.35, 0.9]}>
            <boxGeometry args={[0.04, 0.5, t2.d + 1.8]} />
            <meshPhysicalMaterial color="#B9CDD0" roughness={0.1} transparent opacity={0.4} />
          </mesh>
        </group>
      ))}

      {/* carport détaché (garage), derrière la villa — cliquable */}
      <group
        position={[-3.2, 0, -9]}
        rotation={[0, 0.12, 0]}
        onClick={(e) => { e.stopPropagation(); onSelect("garage"); }}
        onPointerOver={() => (document.body.style.cursor = "pointer")}
        onPointerOut={() => (document.body.style.cursor = "auto")}
      >
        {active === "garage" && (
          <Html center position={[0, 3.6, 0]} zIndexRange={[10, 0]}>
            <div style={label3d}>{labels["garage"]}</div>
          </Html>
        )}
        <mesh position={[0, 0.02, 0.4]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[10, 6.4]} />
          <meshStandardMaterial color={night ? "#55503F" : "#E6D6AE"} roughness={1} />
        </mesh>
        {[[-3.2, -1.6], [0, -1.6], [3.2, -1.6], [-3.2, 1.6], [0, 1.6], [3.2, 1.6]].map(([x, z], i) => (
          <mesh key={i} position={[x, 1.25, z]}>
            <cylinderGeometry args={[0.09, 0.09, 2.5, 10]} />
            <meshStandardMaterial color={night ? "#7E6238" : "#B98F5B"} roughness={0.9} />
          </mesh>
        ))}
        <mesh position={[0, 2.62, 0]}>
          <boxGeometry args={[8.8, 0.14, 4.2]} />
          <meshStandardMaterial map={decking} color={night ? "#7C6748" : "#D8B87E"} roughness={0.95} />
          <Edges threshold={20} color="#8A6F42" />
        </mesh>
        {[...Array(5)].map((_, i) => (
          <mesh key={`j${i}`} position={[-3.4 + i * 1.7, 2.46, 0]}>
            <boxGeometry args={[0.12, 0.18, 4.2]} />
            <meshStandardMaterial color={night ? "#6E5330" : "#A8804E"} />
          </mesh>
        ))}
        <group position={[-5.6, 1.1, 0]}>
          <mesh>
            <boxGeometry args={[2.4, 2.2, 3.6]} />
            <meshStandardMaterial color="#20211F" roughness={0.7} />
            <Edges threshold={20} color="#0F100E" />
          </mesh>
          <mesh position={[0, 0, 1.82]}>
            <boxGeometry args={[2.0, 1.8, 0.06]} />
            <meshStandardMaterial map={cladding} color={night ? "#8A7148" : "#C9A468"} />
          </mesh>
        </group>
        {[-3.4, -1.7, 0, 1.7, 3.4].map((x, i) => (
          <mesh key={`w${i}`} position={[x, 0.55, -2.7]} scale={[0.95, 0.6, 0.7]} rotation={[0.3 * i, i * 0.7, 0]}>
            <dodecahedronGeometry args={[1, 0]} />
            <meshStandardMaterial color={night ? "#5C564B" : "#BCB2A0"} roughness={0.95} flatShading />
          </mesh>
        ))}
      </group>

      {/* maquis bushes & granite rocks */}
      {BUSHES.map(([x, y, z, s], i) => (
        <mesh key={`b${i}`} position={[x, y * s, z]} scale={[s, s * 0.75, s]}>
          <sphereGeometry args={[1, 12, 10]} />
          <meshStandardMaterial color={night ? (i % 2 ? "#2F3A28" : "#39452F") : i % 2 ? "#5F6E4B" : "#71805A"} roughness={1} flatShading />
        </mesh>
      ))}
      {ROCKS.map(([x, z, s], i) => (
        <mesh key={`r${i}`} position={[x, s * 0.5, z]} scale={s} rotation={[0.4 * i, i, 0]}>
          <dodecahedronGeometry args={[1, 0]} />
          <meshStandardMaterial color={night ? "#6E685C" : "#B5AC9C"} roughness={0.95} flatShading />
        </mesh>
      ))}

      <ContactShadows position={[0, 0.01, 2]} opacity={night ? 0.15 : 0.3} scale={48} blur={2.2} far={14} />

      <OrbitControls
        autoRotate={!paused}
        autoRotateSpeed={0.6}
        enablePan={false}
        minDistance={12}
        maxDistance={36}
        minPolarAngle={0.2}
        maxPolarAngle={1.32}
        target={[1, 1.8, 2.5]}
      />
    </>
  );
};

export const Villa3D = ({ active, onSelect, labels, night = false, paused = false }) => (
  <div
    className="absolute inset-0 transition-colors duration-700"
    data-testid="villa-3d-canvas"
    style={{
      background: night
        ? "linear-gradient(180deg, #131A27 0%, #232B36 62%, #2E3239 100%)"
        : "linear-gradient(180deg, #BFD5E2 0%, #E4E0D0 62%, #DED4BC 100%)",
    }}
  >
    <Canvas camera={{ position: [15, 10, 19], fov: 38 }} dpr={[1, 1.8]} gl={{ antialias: true }}>
      <Suspense fallback={null}>
        <Scene active={active} onSelect={onSelect} labels={labels} night={night} paused={paused} />
      </Suspense>
    </Canvas>
  </div>
);
